import { useState, type ReactNode } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import type { Producto, UnidadVenta } from '@abastece/core';
import { db } from '../db/dexie.js';
import { lecturaConDesactivados, lecturaDeCodigo, unidadesDe } from '../lib/catalogo.js';
import type { Lectura } from '../lib/lecturas.js';
import type { Resultado } from '../lib/pitido.js';
import { usePuede } from '../lib/sesion.js';
import { useT } from '../i18n/index.js';
import { etiquetaUnidad } from '../i18n/dominio.js';
import { useNombreProducto } from '../i18n/productos.js';
import { CualEs, type Hallado } from './CualEs.js';
import { EnsenarCodigo } from './EnsenarCodigo.js';
import { Escaner } from './Escaner.js';
import { Chip, Ico } from './Iconos.js';
import { Miniatura } from './Miniatura.js';

export interface OpcionesEscaner {
  titulo?: string;
  /** Keep reading after a product is found, as at the till: one can after another. */
  continuo?: boolean;
  /**
   * Under the name of the product just found, in continuous mode — its price, its stock. With the
   * presentation the code was, when it was one's own: a case's code rang up the case, and the line
   * to describe is the case's, not the product's first.
   */
  describir?: (p: Producto, unidad?: UnidadVenta) => ReactNode;
  /** Beside «Listo» in continuous mode: the figure the scanning is adding up to. */
  resumen?: ReactNode;
  /** Ways out of an unknown code besides teaching it to a product. `cerrar` closes the camera. */
  desconocido?: (codigo: string, cerrar: () => void) => ReactNode;
  /**
   * Under an unknown code, what it probably is (`SugerenciaAbierta`). `listo` is a product of the
   * shop's now carrying the code: what was scanned, as if it had been known all along.
   */
  sugerir?: (codigo: string, listo: (p: Producto, unidad?: UnidadVenta) => void) => ReactNode;
}

type Hoja =
  | { tipo: 'desconocido'; codigo: string }
  | { tipo: 'ensenar'; codigo: string }
  | { tipo: 'varios'; codigo: string; hallados: Hallado[] };

/**
 * Escanear productos: the camera, and what the catalogue says about what it read.
 *
 * A known code is its product, handed to `onProducto` — with the presentation when the code is
 * one presentation's own, so the case's code rings up the case. The two other answers are where this
 * earns its place:
 *
 *   · **A code the shop has never seen** is the normal case for the first few weeks — the
 *     starter catalogue has no barcodes, and nobody is going to type a hundred and twenty of
 *     them in. So the question is asked the first time a can is scanned: which product is
 *     this? Somebody who may edit products picks it by name, the code is saved on it, and it
 *     is never asked again. The catalogue learns its barcodes at the pace the shop sells.
 *   · **A code on two products** — the same goods registered twice, two phones each taught a
 *     code while offline, or a factory that printed one code on two products — is asked about
 *     too, in «¿Cuál es?» (`CualEs`), rather than guessed at.
 */
export function EscanerProductos({
  titulo,
  continuo = false,
  onProducto,
  onCerrar,
  describir,
  resumen,
  desconocido,
  sugerir,
  listaId,
  inactivos = false,
}: OpcionesEscaner & {
  titulo: string;
  onProducto: (p: Producto, unidad?: UnidadVenta) => void;
  onCerrar: () => void;
  /** The prices «¿Cuál es?» shows: the sale's list, or the order's. */
  listaId?: string;
  /** A code nothing on sale answers to finds a product taken off sale: a count's (`lecturaConDesactivados`). */
  inactivos?: boolean;
}) {
  const t = useT();
  const nombre = useNombreProducto();
  const puedeEnsenar = usePuede('editar_productos');
  // `undefined` until the catalogue has loaded: reading before then would call every code unknown.
  const productos = useLiveQuery(() => db.productos.toArray(), []);
  const unidades = useLiveQuery(() => db.unidades.toArray(), [], []);
  const [hoja, setHoja] = useState<Hoja | null>(null);
  const [ultimo, setUltimo] = useState<{ producto: Producto; unidad?: UnidadVenta; vez: number; ensenado: boolean } | null>(
    null,
  );

  function encontrado(p: Producto, ensenado = false, unidad?: UnidadVenta) {
    setHoja(null);
    onProducto(p, unidad);
    if (continuo) setUltimo({ producto: p, ...(unidad ? { unidad } : {}), vez: Date.now(), ensenado });
    else onCerrar();
  }

  function alLeer({ codigo }: Lectura): Resultado {
    const hallados = (inactivos ? lecturaConDesactivados : lecturaDeCodigo)(productos ?? [], unidades, codigo);
    if (hallados.length === 1) {
      encontrado(hallados[0]!.producto, false, hallados[0]!.unidad);
      return 'bien';
    }
    if (hallados.length > 1) {
      // Read right, and the shop knows it: only which of its products is in the hand is asked. It
      // flashed amber and sounded like a code nobody has.
      setHoja({ tipo: 'varios', codigo, hallados });
      return 'bien';
    }
    setHoja({ tipo: 'desconocido', codigo });
    return 'mal';
  }

  const seguir = (
    <button type="button" className="btn-secundario" onClick={() => setHoja(null)}>
      <Ico icono="escanear" />
      {t('escaner.seguir')}
    </button>
  );

  let contenidoHoja: ReactNode = null;
  if (hoja?.tipo === 'desconocido') {
    contenidoHoja = (
      <div className="flex flex-col gap-4 p-5 pt-[max(env(safe-area-inset-top),1.25rem)]">
        <div className="flex items-start gap-3">
          <Chip icono="aviso" tinte="ambar" />
          <div className="min-w-0">
            <h3 className="text-lg font-semibold">{t('escaner.noEsta')}</h3>
            <p className="tabular mt-0.5 text-sm break-all text-slate-400">{hoja.codigo}</p>
          </div>
        </div>
        {sugerir?.(hoja.codigo, (p, unidad) => encontrado(p, true, unidad))}
        {puedeEnsenar ? (
          <>
            <p className="text-sm text-slate-400">{t('escaner.noEstaAyuda')}</p>
            <button
              type="button"
              className="btn-primario"
              onClick={() => setHoja({ tipo: 'ensenar', codigo: hoja.codigo })}
            >
              <Ico icono="enlazar" />
              {t('escaner.esDeUnProducto')}
            </button>
          </>
        ) : (
          <p className="text-sm text-slate-400">{t('escaner.pidaAlDueno')}</p>
        )}
        {desconocido?.(hoja.codigo, onCerrar)}
        {seguir}
      </div>
    );
  } else if (hoja?.tipo === 'ensenar') {
    contenidoHoja = (
      <div className="flex flex-col gap-4 p-5 pt-[max(env(safe-area-inset-top),1.25rem)]">
        <header className="flex items-center gap-2">
          <button
            type="button"
            className="-ml-2 flex size-11 shrink-0 items-center justify-center rounded-xl text-slate-400 hover:bg-slate-800"
            aria-label={t('comun.volver')}
            onClick={() => setHoja({ tipo: 'desconocido', codigo: hoja.codigo })}
          >
            <Ico icono="atras" />
          </button>
          <h3 className="text-lg font-semibold">{t('escaner.cualProducto')}</h3>
        </header>
        <p className="text-sm text-slate-400">{t('escaner.cualProductoAyuda', { codigo: hoja.codigo })}</p>
        {/* The product, then the presentation the code is printed on when it has several. */}
        <EnsenarCodigo codigo={hoja.codigo} onListo={(p, unidad) => encontrado(p, true, unidad)} />
      </div>
    );
  } else if (hoja?.tipo === 'varios') {
    contenidoHoja = (
      <div className="p-5 pt-[max(env(safe-area-inset-top),1.25rem)]">
        <CualEs
          titulo
          codigo={hoja.codigo}
          hallados={hoja.hallados}
          {...(listaId ? { listaId } : {})}
          onElegido={({ producto, unidad }) => encontrado(producto, false, unidad)}
          onSeguir={() => setHoja(null)}
        />
      </div>
    );
  }

  // Continuous mode says what each read did, big enough to catch from the corner of an eye.
  const pie = continuo && (
    <div className="flex flex-col gap-3">
      {ultimo ? (
        <div
          key={ultimo.vez}
          className="brinca flex items-center gap-3 rounded-2xl bg-emerald-500/10 p-2 pr-3 ring-1 ring-emerald-500/30 ring-inset"
        >
          <Miniatura producto={ultimo.producto} tamano="md" />
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium">
              {nombre(ultimo.producto)}
              {/* A case's code rang up the case: said where the eye lands, not only in the small print. */}
              {ultimo.unidad && ultimo.unidad.id !== unidadesDe(unidades, ultimo.producto.id)[0]?.id && (
                <span className="text-emerald-300"> · {etiquetaUnidad(ultimo.unidad.nombre, ultimo.unidad.nombre)}</span>
              )}
            </p>
            <div className="text-xs text-emerald-300">
              {ultimo.ensenado && <p>{t('escaner.codigoGuardado')}</p>}
              {describir?.(ultimo.producto, ultimo.unidad)}
            </div>
          </div>
          <Ico icono="listo" className="size-6 shrink-0 text-emerald-400" />
        </div>
      ) : (
        <p className="text-sm text-slate-400">{t('escaner.continuoAyuda')}</p>
      )}
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">{resumen}</div>
        <button type="button" className="btn-primario px-6" onClick={onCerrar}>
          <Ico icono="listo" />
          {t('escaner.listo')}
        </button>
      </div>
    </div>
  );

  return (
    <Escaner
      titulo={titulo}
      onLeido={alLeer}
      onCerrar={onCerrar}
      pausado={productos === undefined}
      hoja={contenidoHoja}
      pie={pie}
    />
  );
}
